import { useCallback, useMemo } from 'react';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { logger } from '../modules/logger';
import { AppRoutePath } from '../config/constants';
import {
  dispatchExtensionEvent,
  transitionRouteEvent,
  closeWindowEvent,
} from '../domains/extension/extensionEventCreators';
import { ExtWindowName } from './useExtensionState';
import usePatientJacketView, {
  PATIENT_JACKET_VIEWS,
} from 'domains/worklist/Worklist/PatientJacket/hooks/usePatientJacketView';
import { useNavigate } from 'react-router-dom';
import { openPopup } from 'utils/openPopup';

type UseManagePatientJacketReturn = {
  managePatientJacket: (params?: {
    nextCaseSmid?: string | null | undefined;
    forceOpen?: boolean;
  }) => void;
};

export const useManagePatientJacket = (): UseManagePatientJacketReturn => {
  const navigate = useNavigate();
  const { data } = useCurrentUser();
  const me = data?.me;
  const shouldPatientJacketBeDocked = me?.worklistSettings?.isPatientJacketDocked ?? true;
  const openPatientJacketOnRead = me?.worklistSettings?.openPatientJacketOnRead ?? true;

  const { isDocked, isUndocked, view } = usePatientJacketView();
  const isPatientJacketOpen = view !== PATIENT_JACKET_VIEWS.NONE;
  const isCurrentWindowWorklist = window.location.pathname.includes('worklist');

  const openDockedPatientJacket = useCallback(
    (nextCaseSmid: string) => {
      if (isCurrentWindowWorklist) {
        navigate(AppRoutePath.PATIENT_JACKET.replace(':smid', nextCaseSmid));
      } else {
        dispatchExtensionEvent(
          transitionRouteEvent({
            target: ExtWindowName.Worklist,
            path: `${AppRoutePath.PATIENT_JACKET.replace(':smid', nextCaseSmid)}`,
          })
        );
      }
    },
    [isCurrentWindowWorklist, navigate]
  );

  const closePatientJacket = useCallback(() => {
    if (isCurrentWindowWorklist) {
      navigate(AppRoutePath.WORKLIST);
    } else if (isDocked) {
      dispatchExtensionEvent(
        transitionRouteEvent({
          target: ExtWindowName.Worklist,
          path: AppRoutePath.WORKLIST,
        })
      );
    } else {
      dispatchExtensionEvent(
        closeWindowEvent({
          target: ExtWindowName.Patient_Jacket,
        })
      );
    }
  }, [isCurrentWindowWorklist, isDocked, navigate]);

  const managePatientJacket = useCallback(
    ({ nextCaseSmid = null, forceOpen = false } = {}) => {
      if (nextCaseSmid == null) {
        logger.info('[useManagePatientJacket] sending closePatientJacket event', {
          nextCaseSmid,
          openPatientJacketOnRead,
          isPatientJacketOpen,
        });
        closePatientJacket();
      } else if (isPatientJacketOpen) {
        logger.info('[useManagePatientJacket] sending nextPatientJacket event', {
          nextCaseSmid,
          openPatientJacketOnRead,
          isPatientJacketOpen,
        });
        if (isUndocked) {
          dispatchExtensionEvent(
            transitionRouteEvent({
              target: ExtWindowName.Patient_Jacket,
              path: `${AppRoutePath.PATIENT_JACKET_UNDOCKED.replace(':smid', nextCaseSmid)}`,
            })
          );
        } else {
          openDockedPatientJacket(nextCaseSmid);
        }
      } else if (forceOpen || openPatientJacketOnRead) {
        logger.info('[useManagePatientJacket] sending openPatientJacket event', {
          nextCaseSmid,
          openPatientJacketOnRead,
          isPatientJacketOpen,
        });
        if (shouldPatientJacketBeDocked) {
          openDockedPatientJacket(nextCaseSmid);
        } else {
          const url = `${AppRoutePath.PATIENT_JACKET_UNDOCKED.replace(':smid', nextCaseSmid)}`;
          openPopup(url);
        }
      }
    },
    [
      isPatientJacketOpen,
      openPatientJacketOnRead,
      closePatientJacket,
      isUndocked,
      openDockedPatientJacket,
      shouldPatientJacketBeDocked,
    ]
  );

  // @ts-expect-error [EN-10263] - TS2322 - Type '{ managePatientJacket: ({ nextCaseSmid, forceOpen }?: { nextCaseSmid?: null | undefined; forceOpen?: boolean | undefined; }) => void; }' is not assignable to type 'UseManagePatientJacketReturn'.
  return useMemo(() => ({ managePatientJacket }), [managePatientJacket]);
};
